import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { DialogueIcon, LockIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Empty,
	Heading,
	IconWrapper,
	NumberedSubheading,
	Paragraph,
	Pill,
	QuillText,
	Skeleton,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import GoalCard from '../../goals/GoalCard';
import { FeedbackQuestionMoreInformation } from '../FeedbackQuestionMoreInformation';
import FeedbackResultAnswer from './FeedbackResultAnswer';

const Container = styled(Box)`
	page-break-inside: avoid;
`;

const Answers = styled(Box)`
	@media print {
		margin-top: ${tokens.gap.section}px !important;
	}
`;

const Answer = styled(Box)`
	@media print {
		margin-top: ${tokens.gap.list}px !important;
	}

	&:first-of-type {
		@media print {
			margin-top: 0 !important;
		}
	}
`;

const Others = styled(Link)`
	color: ${(props) => props.theme.tokens.text.link};

	&:hover {
		text-decoration: underline;

		&:active {
			color: ${(props) => props.theme.tokens.text.primary};
		}
	}
`;

type Props = {
	request: graphql.FeedbackRequest;
	question: graphql.FeedbackRequestQuestion;
	response?: graphql.FeedbackResponse;
	responses: graphql.FeedbackResponse[];
	small?: boolean;
	isSubject?: boolean;
	index?: number;
};

export default function FeedbackResultQuestion(props: Props) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	// Questions hidden from the subject are never rendered to the subject
	// themselves; privileged viewers see the answers with an explanatory badge.
	const showHiddenBadge = !(props.question.canSubjectView ?? true) && !props.isSubject;

	const answers = (props.response ? [props.response] : props.responses)
		.filter((response) => response.status === graphql.FeedbackResponseStatus.Shared)
		.map((response) => {
			const answer = response.answers?.find(({ questionID }) => questionID === props.question.id);
			return {
				response,
				answer,
			};
		})
		.filter(({ answer }) => !!answer);

	const shared = props.responses
		.filter((response) => response.id !== props.response?.id)
		.filter((response) => response.status === graphql.FeedbackResponseStatus.Shared).length;

	const questionText = props.question.localizedText
		? localize(props.question.localizedText).text
		: props.question.text;

	const questionCopy = (
		<>
			{props.small ? <Paragraph>{questionText}</Paragraph> : <Heading>{questionText}</Heading>}
			{props.question.subTitle && (
				<Paragraph mt={props.small ? 0 : 2}>{props.question.subTitle}</Paragraph>
			)}
			{showHiddenBadge && (
				<Box mt={2}>
					<Pill tint="primary" small>
						<Flex alignItems="center">
							<IconWrapper mr={1} size={14} color="primary">
								<LockIcon />
							</IconWrapper>
							{t('feedback.results.question.hiddenFromEmployee.badge')}
						</Flex>
					</Pill>
				</Box>
			)}
			{!QuillText.isEmpty(props.question.additionalDetails) && (
				<FeedbackQuestionMoreInformation additionalDetails={props.question.additionalDetails} />
			)}
			{props.question.goal && (
				<Box mt={2}>
					{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
					<GoalCard small goal={props.question.goal as any} />
				</Box>
			)}
			{props.question.goalRevision && (
				<Box mt={2}>
					<GoalCard small goal={props.question.goalRevision} modal />
				</Box>
			)}
			{props.question.competency && (
				<Subheading mt={2}>{localize(props.question.competency.title).text}</Subheading>
			)}
		</>
	);

	return (
		<Container>
			{props.small ? (
				questionCopy
			) : props.index != null ? (
				<NumberedSubheading index={props.index}>{questionCopy}</NumberedSubheading>
			) : (
				<Box mb={3}>{questionCopy}</Box>
			)}
			<Answers mt={props.small ? 2 : 3} pl={props.small ? 0 : '44px'}>
				{answers.length > 0 ? (
					answers.map(({ response, answer }) => (
						<Answer key={response.id} mt={props.small ? 2 : 3}>
							<FeedbackResultAnswer
								response={response}
								recipient={response.recipient}
								question={props.question}
								answer={answer!}
								small={props.small}
							/>
						</Answer>
					))
				) : (
					<Skeleton.Empty
						contents={
							<Empty
								icon={<DialogueIcon />}
								title={t('feedback.results.question.empty.title')}
								small
							/>
						}
					>
						<Box mt={3}>
							<FeedbackResultAnswer.Skeleton small={props.small} />
						</Box>
					</Skeleton.Empty>
				)}
			</Answers>
			{shared > 0 && props.response && (
				<Box mt={2} ml={40}>
					<Others to={`/feedback/${props.request.id}`}>
						{t('feedback.results.question.more', { count: shared })}
					</Others>
				</Box>
			)}
		</Container>
	);
}

function FeedbackResultQuestionSkeleton() {
	return (
		<Container>
			<Heading.Skeleton />
			<Skeleton.Repeat count={3}>
				<Box mt={3}>
					<FeedbackResultAnswer.Skeleton />
				</Box>
			</Skeleton.Repeat>
		</Container>
	);
}

FeedbackResultQuestion.Skeleton = FeedbackResultQuestionSkeleton;
